import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { hasAnyPermission, hasPermission, normalizePermissions } from './permissionUtils';

const API_URL = process.env.REACT_APP_API_URL;

const AuthContext = createContext(null);

function parseStoredUser() {
  const raw = localStorage.getItem('uzytkownik');
  if (!raw || raw === 'undefined') return null;
  try {
    return JSON.parse(raw);
  } catch (_error) {
    return null;
  }
}

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);

  const fetchPermissionsFromApi = async (token, fallbackUser) => {
    try {
      const res = await axios.get(`${API_URL}/uzytkownicy/me/permissions`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (Array.isArray(res?.data?.permissions)) {
        return res.data.permissions;
      }
    } catch (_error) {}
    return [];
  };

  useEffect(() => {
    const hydrate = async () => {
      const token = localStorage.getItem('token');
      const refreshToken = localStorage.getItem('refreshToken');
      const storedUser = parseStoredUser();

      if (!token || !storedUser) {
        setUser(null);
        setLoading(false);
        return;
      }

      try {
        const decoded = jwtDecode(token);
        const now = Date.now() / 1000;

        if (decoded.exp < now) {
          if (!refreshToken) {
            setUser(null);
            setLoading(false);
            return;
          }

          const res = await axios.post(`${API_URL}/refresh`, { refreshToken });
          const newToken = res?.data?.accessToken;
          if (!newToken) {
            setUser(null);
            setLoading(false);
            return;
          }

          localStorage.setItem('token', newToken);
          const permissions = await fetchPermissionsFromApi(newToken, storedUser);

          const nextUser = { ...storedUser, permissions };
          localStorage.setItem('uzytkownik', JSON.stringify(nextUser));
          localStorage.setItem('permissions', JSON.stringify(permissions));
          setUser(nextUser);
          setLoading(false);
          return;
        }

        const permissionsFromToken = await fetchPermissionsFromApi(token, storedUser);
        const nextUser = { ...storedUser, permissions: permissionsFromToken };
        localStorage.setItem('uzytkownik', JSON.stringify(nextUser));
        localStorage.setItem('permissions', JSON.stringify(permissionsFromToken));
        setUser(nextUser);
      } catch (_err) {
        setUser(null);
      } finally {
        setLoading(false);
      }
    };

    hydrate();
  }, []);

  const login = (token, refreshToken, userData) => {
    const permissions = Array.isArray(userData?.permissions)
      ? normalizePermissions({ permissions: userData.permissions })
      : [];
    const nextUser = { ...userData, permissions };

    localStorage.setItem('token', token);
    if (refreshToken) {
      localStorage.setItem('refreshToken', refreshToken);
    }
    localStorage.setItem('uzytkownik', JSON.stringify(nextUser));
    localStorage.setItem('permissions', JSON.stringify(permissions));

    setUser(nextUser);

    fetchPermissionsFromApi(token, nextUser)
      .then((freshPermissions) => {
        const updatedUser = { ...nextUser, permissions: freshPermissions };
        localStorage.setItem('uzytkownik', JSON.stringify(updatedUser));
        localStorage.setItem('permissions', JSON.stringify(freshPermissions));
        setUser(updatedUser);
      })
      .catch(() => {});
  };

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('uzytkownik');
    localStorage.removeItem('permissions');
    setUser(null);
  };

  const value = useMemo(() => ({
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
    permissions: normalizePermissions(user),
    hasPermission: (permission) => hasPermission(user, permission),
    hasAnyPermission: (permissions) => hasAnyPermission(user, permissions),
  }), [user, loading]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
